import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FilterFunnel02 } from '@untitledui/icons/FilterFunnel02';
import { Plus } from '@untitledui/icons/Plus';
import { Route as RouteIcon } from '@untitledui/icons/Route';
import { SearchLg } from '@untitledui/icons/SearchLg';
import { Users01 } from '@untitledui/icons/Users01';
import { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import type { ProgramDto, Project } from '@/api/core-api/types.gen';

import { ProgramStatus, ProjectSortField } from '@/api/core-api/types.gen';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Toggle } from '@/components/base/toggle/toggle';
import { FeaturedIcon } from '@/components/foundations/featured-icon/featured-icon';
import { ResearcherShell } from '@/components/navigation/researcher-shell';
import { ProgramFormModal } from '@/components/programs/program-form-modal';
import { ResearchHeader } from '@/components/programs/research-header';
import { useCreateProgram } from '@/hooks/api/use-program-mutations';
import { programsQueryOptions } from '@/hooks/api/use-programs';
import {
  MAX_PAGE_SIZE,
  teamProjectsQueryOptions,
} from '@/hooks/api/use-team-projects';

import { NewProjectSetupModal } from '../projects/-components/new-project-setup-modal';
import { ProgramSearchInput } from './-components/program-search-input';
import { resolveProgramProjects } from './-program-projects';

const programsSearchSchema = z.object({
  /** Archived programs are hidden by default */
  archived: z.boolean().catch(false).default(false),
  q: z.coerce.string().max(255).optional().catch(undefined),
});

export const Route = createFileRoute('/researchers/_authenticated/programs/')({
  validateSearch: programsSearchSchema,
  component: ProgramsPage,
  head: () => ({ meta: [{ title: t`Programs` }] }),
});

function ProgramsPage() {
  const { user, activeDashboard } = Route.useRouteContext();
  const teamId = activeDashboard.team ?? '';
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);

  const createProgram = useCreateProgram(teamId, user.id);

  const {
    data: programs,
    isPending,
    isError,
    refetch,
  } = useQuery({
    ...programsQueryOptions(teamId, user.id),
    enabled: !!teamId,
  });

  /**
   * The ids of the programs on this page, sorted for a stable query key.
   *
   * One request fetches every project across all of them (`programIds` filter),
   * grouped per card by `project.programId` below — rather than one request per
   * program.
   */
  const programIds = useMemo(
    () => (programs ?? []).map((program) => program.id).toSorted(),
    [programs],
  );

  const { data: projectsData } = useQuery({
    ...teamProjectsQueryOptions({
      teamId,
      authUserId: user.id,
      page: 1,
      // Bounded by the endpoint's ceiling. A team whose programs collectively
      // hold more projects than this would under-report on this page; the detail
      // page queries one program at a time and is unaffected.
      pageSize: MAX_PAGE_SIZE,
      programIds,
      sortBy: ProjectSortField.CREATED_AT,
      sortDir: 'desc',
    }),
    enabled: !!teamId && programIds.length > 0,
  });

  const teamProjects = projectsData?.results ?? [];

  const visiblePrograms = useMemo(() => {
    const all = search.archived
      ? (programs ?? [])
      : (programs ?? []).filter(
          (program) => program.status !== ProgramStatus.ARCHIVED,
        );
    const query = search.q?.trim().toLocaleLowerCase();
    if (!query) return all;
    return all.filter((program) =>
      program.name.toLocaleLowerCase().includes(query),
    );
  }, [programs, search.archived, search.q]);

  const commitSearch = useCallback(
    (q: string | undefined) => {
      if (q === search.q) return;
      void navigate({
        search: (previous) => ({ ...previous, q }),
        replace: true,
      });
    },
    [navigate, search.q],
  );

  const hasNoPrograms = !isPending && !isError && (programs ?? []).length === 0;
  const hasSearchQuery = (search.q?.trim().length ?? 0) > 0;

  const showToolbar = !isPending && !isError && !hasNoPrograms;

  return (
    <ResearcherShell user={user} activeDashboard={activeDashboard}>
      <div className="px-6 py-8">
        <div className="mx-auto max-w-360">
          <ResearchHeader
            activeTab="programs"
            teamId={teamId}
            authUserId={user.id}
            onNewProject={() => setIsCreateProjectOpen(true)}
            onNewProgram={() => setIsCreateOpen(true)}
          />

          {showToolbar && (
            <div className="mt-6 flex items-center justify-between gap-3">
              <div className="max-w-100 min-w-50 flex-1">
                <ProgramSearchInput
                  urlQuery={search.q ?? ''}
                  onCommit={commitSearch}
                />
              </div>
              <Toggle
                label={t`Show archived`}
                size="sm"
                isSelected={search.archived}
                onToggle={() =>
                  void navigate({
                    search: (previous) => ({
                      ...previous,
                      archived: !search.archived,
                    }),
                    replace: true,
                  })
                }
              />
            </div>
          )}

          <div className="mt-6">
            {isError ? (
              <div className="rounded-xl border border-secondary bg-primary p-12 text-center">
                <p className="text-sm text-tertiary">
                  <Trans>Could not load programs.</Trans>
                </p>
                <Button
                  color="link-color"
                  size="sm"
                  className="mt-2"
                  onPress={() => refetch()}
                >
                  <Trans>Try again</Trans>
                </Button>
              </div>
            ) : isPending ? (
              <div className="flex flex-col gap-4">
                {['a', 'b', 'c'].map((key) => (
                  <div
                    key={key}
                    className="h-28 animate-pulse rounded-xl bg-primary ring-1 ring-secondary"
                  />
                ))}
              </div>
            ) : hasNoPrograms ? (
              <ProgramsEmptyState onCreate={() => setIsCreateOpen(true)} />
            ) : visiblePrograms.length === 0 ? (
              <div className="rounded-xl border border-secondary bg-primary p-12 text-center">
                <p className="text-sm text-tertiary">
                  {hasSearchQuery ? (
                    <Trans>No programs matching your search.</Trans>
                  ) : (
                    <Trans>
                      Every program on this team is archived. Turn on “Show
                      archived” to see them.
                    </Trans>
                  )}
                </p>
              </div>
            ) : (
              <ul className="flex flex-col gap-4">
                {visiblePrograms.map((program) => (
                  <li key={program.id}>
                    <ProgramCard
                      program={program}
                      teamProjects={teamProjects}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <NewProjectSetupModal
        isOpen={isCreateProjectOpen}
        onOpenChange={setIsCreateProjectOpen}
        organizationId={activeDashboard.organization}
        useStudyTerminology
      />

      <ProgramFormModal
        isOpen={isCreateOpen}
        onOpenChange={(open) => {
          setIsCreateOpen(open);
          if (!open) createProgram.reset();
        }}
        isSaving={createProgram.isPending}
        errorMessage={
          createProgram.isError ? t`Could not create the program.` : undefined
        }
        onSubmit={(values) => {
          createProgram.mutate(values, {
            onSuccess: () => setIsCreateOpen(false),
          });
        }}
      />
    </ResearcherShell>
  );
}

/**
 * First-run empty state: a value pitch rather than a "nothing here" notice,
 * since a team with zero programs has not yet decided the feature is for them.
 */
function ProgramsEmptyState({ onCreate }: { onCreate: () => void }) {
  const benefits = [
    { label: t`Re-invite the same participants`, Icon: Users01 },
    { label: t`See each person's journey across studies`, Icon: RouteIcon },
    { label: t`Re-screen only when you need to`, Icon: FilterFunnel02 },
    { label: t`All insights in one searchable place`, Icon: SearchLg },
  ] as const;

  return (
    <div
      className="flex flex-col items-center rounded-xl bg-primary px-6 py-16 ring-1 ring-secondary"
      data-testid="programs-empty-state"
    >
      <h2 className="mt-6 text-center font-heading text-display-xs font-semibold text-primary">
        <Trans>Every study builds on the last</Trans>
      </h2>
      <ul className="mt-6 flex flex-col gap-6">
        {benefits.map((benefit) => (
          <li key={benefit.label} className="flex items-center gap-3">
            <FeaturedIcon
              icon={benefit.Icon}
              size="sm"
              theme="modern"
              color="gray"
            />
            <span className="text-md font-medium text-tertiary">
              {benefit.label}
            </span>
          </li>
        ))}
      </ul>
      <Button
        className="mt-8"
        color="primary"
        size="lg"
        iconLeading={Plus}
        onClick={onCreate}
        data-testid="programs-empty-state-create"
      >
        {t`Create your first program`}
      </Button>
    </div>
  );
}

function ProgramCard({
  program,
  teamProjects,
}: {
  program: ProgramDto;
  teamProjects: Project[];
}) {
  const { projectCount, breakdown } = resolveProgramProjects(
    program.id,
    teamProjects,
  );
  const isArchived = program.status === ProgramStatus.ARCHIVED;

  return (
    <Link
      to="/researchers/programs/$programId"
      params={{ programId: program.id }}
      className="block rounded-xl bg-primary p-5 ring-1 ring-secondary transition hover:ring-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-md font-semibold text-primary">
              {program.name}
            </h2>
            {isArchived && (
              <Badge color="gray" size="sm" type="pill-color">
                <Trans>Archived</Trans>
              </Badge>
            )}
          </div>
          {program.description && (
            <p className="mt-1 line-clamp-2 text-sm text-tertiary">
              {program.description}
            </p>
          )}
        </div>
        <span className="shrink-0 text-sm text-tertiary">
          {projectCount === 1 ? t`1 study` : t`${projectCount} studies`}
        </span>
      </div>

      {/* What is happening inside the program. Distinct from the program's own
          active/archived status above — a program is active or archived, its
          projects are recruiting or paused or complete. */}
      {breakdown.length > 0 && (
        <div className="mt-4 flex w-fit max-w-full flex-wrap items-center overflow-clip rounded-full border border-secondary">
          {breakdown.map((entry) => (
            <Badge
              key={entry.label}
              color={entry.color}
              size="sm"
              type="pill-color"
              className="rounded-none border-secondary ring-0 not-last:border-r first:rounded-l-full last:rounded-r-full"
            >
              {`${entry.count} ${entry.label.toLowerCase()}`}
            </Badge>
          ))}
        </div>
      )}

      {projectCount === 0 && (
        <p className="mt-4 text-sm text-tertiary">
          <Trans>No studies yet — add the first one to get started.</Trans>
        </p>
      )}
    </Link>
  );
}
